import React from 'react';
import {
  AgeCategoryDraft,
  WeightCategoryDraft,
  formatAgeDraftLabel,
  formatWeightDraftLabel,
} from '../../utils/categoryResolver';
import './CategoryLimitsEditor.scss';

interface WeightEditorProps {
  categories: WeightCategoryDraft[];
  onChange: (categories: WeightCategoryDraft[]) => void;
}

interface AgeEditorProps {
  categories: AgeCategoryDraft[];
  onChange: (categories: AgeCategoryDraft[]) => void;
}

const reindex = <T extends { sortOrder: number }>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, sortOrder: index + 1 }));

export const WeightCategoriesEditor: React.FC<WeightEditorProps> = ({ categories, onChange }) => {
  const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  const updateAt = (index: number, patch: Partial<WeightCategoryDraft>) => {
    const next = ordered.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(reindex(next));
  };

  const removeAt = (index: number) => {
    onChange(reindex(ordered.filter((_, i) => i !== index)));
  };

  const addCategory = () => {
    onChange(reindex([...ordered, { sortOrder: ordered.length + 1, maxWeight: undefined, isOpenEnded: false }]));
  };

  const addOpenEnded = () => {
    if (ordered.some(c => c.isOpenEnded)) return;
    onChange(reindex([...ordered, { sortOrder: ordered.length + 1, isOpenEnded: true }]));
  };

  return (
    <div className="category-limits-editor">
      {ordered.map((cat, index) => (
        <div key={cat.sortOrder} className="category-limits-editor__row">
          <div className="category-limits-editor__fields">
            {cat.isOpenEnded ? (
              <span className="category-limits-editor__label">Тяжёлый вес (без ограничения сверху)</span>
            ) : (
              <>
                <label htmlFor={`weight-max-${cat.sortOrder}`}>До</label>
                <input
                  id={`weight-max-${cat.sortOrder}`}
                  className="category-limits-editor__number"
                  type="number"
                  min={1}
                  step={0.1}
                  value={cat.maxWeight ?? ''}
                  onChange={(e) => updateAt(index, { maxWeight: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="42"
                />
                <span className="category-limits-editor__unit">кг</span>
              </>
            )}
          </div>
          <div className="category-limits-editor__meta">
            <span className="category-limits-editor__preview">{formatWeightDraftLabel(cat, ordered)}</span>
            <button type="button" className="category-limits-editor__delete" onClick={() => removeAt(index)}>
              Удалить
            </button>
          </div>
        </div>
      ))}
      <div className="category-limits-editor__actions">
        <button type="button" className="category-limits-editor__add" onClick={addCategory}>
          + Добавить категорию
        </button>
        {!ordered.some(c => c.isOpenEnded) && (
          <button type="button" className="category-limits-editor__add" onClick={addOpenEnded}>
            + Тяжёлый вес
          </button>
        )}
      </div>
    </div>
  );
};

export const AgeCategoriesEditor: React.FC<AgeEditorProps> = ({ categories, onChange }) => {
  const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  const updateAt = (index: number, patch: Partial<AgeCategoryDraft>) => {
    const next = ordered.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(reindex(next));
  };

  const removeAt = (index: number) => {
    onChange(reindex(ordered.filter((_, i) => i !== index)));
  };

  const addCategory = () => {
    onChange(reindex([...ordered, { sortOrder: ordered.length + 1, maxAge: undefined, isOpenEnded: false, name: '' }]));
  };

  const addOpenEnded = () => {
    if (ordered.some(c => c.isOpenEnded)) return;
    onChange(reindex([...ordered, { sortOrder: ordered.length + 1, isOpenEnded: true, name: '' }]));
  };

  return (
    <div className="category-limits-editor">
      {ordered.map((cat, index) => (
        <div key={cat.sortOrder} className="category-limits-editor__row">
          <div className="category-limits-editor__fields">
            {cat.isOpenEnded ? (
              <>
                <label htmlFor={`age-name-${cat.sortOrder}`} className="category-limits-editor__field-label">
                  Название категории *
                </label>
                <input
                  id={`age-name-${cat.sortOrder}`}
                  type="text"
                  value={cat.name ?? ''}
                  onChange={(e) => updateAt(index, { name: e.target.value })}
                  placeholder="Например: Юноши, Мужчины, Девушки"
                  className="category-limits-editor__name"
                  required
                />
                <span className="category-limits-editor__label">Без верхнего предела по возрасту</span>
              </>
            ) : (
              <>
                <label htmlFor={`age-name-${cat.sortOrder}`} className="category-limits-editor__field-label">
                  Название категории *
                </label>
                <input
                  id={`age-name-${cat.sortOrder}`}
                  type="text"
                  value={cat.name ?? ''}
                  onChange={(e) => updateAt(index, { name: e.target.value })}
                  placeholder="Например: Юноши, Мужчины, Девушки"
                  className="category-limits-editor__name"
                  required
                />
                <label htmlFor={`age-max-${cat.sortOrder}`}>До</label>
                <input
                  id={`age-max-${cat.sortOrder}`}
                  className="category-limits-editor__number"
                  type="number"
                  min={1}
                  value={cat.maxAge ?? ''}
                  onChange={(e) => updateAt(index, { maxAge: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  placeholder="18"
                />
                <span className="category-limits-editor__unit">лет</span>
              </>
            )}
          </div>
          <div className="category-limits-editor__meta">
            <span className="category-limits-editor__preview">{formatAgeDraftLabel(cat, ordered)}</span>
            <button type="button" className="category-limits-editor__delete" onClick={() => removeAt(index)}>
              Удалить
            </button>
          </div>
        </div>
      ))}
      <div className="category-limits-editor__actions">
        <button type="button" className="category-limits-editor__add" onClick={addCategory}>
          + Добавить категорию
        </button>
        {!ordered.some(c => c.isOpenEnded) && (
          <button type="button" className="category-limits-editor__add" onClick={addOpenEnded}>
            + Без ограничения
          </button>
        )}
      </div>
    </div>
  );
};
